import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EFieldState } from 'constants/enums';

import InputSelectDropdown, {
  InputSelectDropdownOption
} from '../InputSelectDropdown/InputSelectDropdown';

import { CreateOptionContent } from './CreateOptionContent';
import { enhanceOptionsWithActions } from './utils/enhanceOptionsWithActions';

type OptionHandler = (option: InputSelectDropdownOption) => void;

const EditIcon = <FontAwesomeIcon icon="fa-regular fa-pen-to-square" className="mr-2 h-4 w-4 shrink-0 opacity-50" />;

type InputSelectDropdownWithActionsProps = {
  options: InputSelectDropdownOption[];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  noSearchResultsText?: string;
  defaultSelected?: InputSelectDropdownOption;
  onOptionChange?: (value: string) => void;
  state?: EFieldState;
  onEdit?: OptionHandler;
  onDelete?: OptionHandler;
  onCreate?: (value: string) => void;
};

export const InputSelectDropdownWithActions: FC<InputSelectDropdownWithActionsProps> = ({
  options,
  onEdit,
  onDelete,
  onCreate,
  noSearchResultsText,
  ...restProps
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editingOption, setEditingOption] = useState<InputSelectDropdownOption | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (editMode && editingOption) {
      setSearchValue(editingOption.label);
    }
  }, [editMode, editingOption]);

  const resetState = useCallback(() => {
    setEditMode(false);
    setEditingOption(null);
    setSearchValue('');
  }, []);

  const handleStartEdit = useCallback((option: InputSelectDropdownOption, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOption(option);
    setEditMode(true);
  }, []);

  const handleDeleteClick = useCallback(
    (option: InputSelectDropdownOption, e: React.MouseEvent) => {
      e.stopPropagation();
      setPopoverOpen(false);
      onDelete?.(option);
    },
    [onDelete]
  );

  const canCreate = useCallback(
    (value: string) => {
      if (!onCreate) return false;
      const trimmed = value.trim().toLowerCase();
      const matchesOption = trimmed && options.some(opt => opt.label.toLowerCase().includes(trimmed));
      return !!(trimmed && !matchesOption);
    },
    [onCreate, options]
  );

  const isValueDuplicate = useCallback(
    (value: string, excludeValue?: string | number) =>
      options.some(opt => opt.label.toLowerCase() === value.trim().toLowerCase() && opt.value !== excludeValue),
    [options]
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, value: string, clearSearch: () => void, closeDropdown: () => void) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (editMode && editingOption && onEdit) {
          const trimmed = value.trim();
          if (isValueDuplicate(trimmed, editingOption.value)) return;
          onEdit({ ...editingOption, label: trimmed });
          closeDropdown();
          resetState();
        } else if (canCreate(value)) {
          onCreate?.(value.trim());
          clearSearch();
          closeDropdown();
        }
      } else if (e.key === 'Escape' && editMode) {
        resetState();
        clearSearch();
      }
    },
    [canCreate, onCreate, editMode, editingOption, onEdit, isValueDuplicate, resetState]
  );

  const optionsWithActions = useMemo(
    () =>
      enhanceOptionsWithActions({
        options,
        onEdit,
        onDelete,
        onStartEdit: handleStartEdit,
        onDeleteClick: handleDeleteClick
      }),
    [options, handleStartEdit, handleDeleteClick, onEdit, onDelete]
  );

  return (
    <InputSelectDropdown
      {...restProps}
      options={optionsWithActions}
      shouldCapitalizeOptions={false}
      emptyStateRenderer={(currentSearchValue, clearSearch, closeDropdown) => (
        <CreateOptionContent
          currentSearchValue={currentSearchValue}
          clearSearch={clearSearch}
          closeDropdown={closeDropdown}
          editMode={editMode}
          canCreate={canCreate}
          onCreate={onCreate}
          noSearchResultsText={noSearchResultsText}
        />
      )}
      noSearchResultsText={noSearchResultsText}
      emptyStateClassName="py-1 text-left"
      onSearchKeyDown={handleSearchKeyDown}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      hideOptions={editMode}
      searchInputIcon={editMode ? EditIcon : undefined}
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
    />
  );
};
